import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { AdminNav } from '../../components/AdminNav';

export const AdminListingRequests = () => {
  const navigate = useNavigate();
  const { admin } = useAdmin();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!admin) {
      navigate('/admin/login');
      return;
    }
    fetchRequests();
  }, [admin, filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const url = filter === 'all' 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/listing-requests`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/listing-requests?status=${filter}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch requests');

      const data = await response.json();
      setRequests(data.requests);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setMessage('Failed to load listing requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (requestId, action, notes = '') => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/listing-requests/${requestId}/${action}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ notes })
        }
      );

      if (!response.ok) throw new Error(`Failed to ${action} request`);

      const data = await response.json();
      setMessage(data.message);
      setShowModal(false);
      fetchRequests();
    } catch (error) {
      console.error(`Error ${action}ing request:`, error);
      setMessage(`Failed to ${action} request`);
    } finally {
      setActionLoading(false);
    }
  };

  const RequestModal = () => {
    const [notes, setNotes] = useState('');

    if (!selectedRequest) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Review Listing Request</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Full Name</p>
                  <p className="font-semibold">{selectedRequest.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">License Number</p>
                  <p className="font-semibold">{selectedRequest.license_number} ({selectedRequest.state})</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-semibold">{selectedRequest.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-semibold">{selectedRequest.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-semibold">{selectedRequest.city}, {selectedRequest.state} {selectedRequest.zip_code}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Experience</p>
                  <p className="font-semibold">{selectedRequest.years_experience} years</p>
                </div>
                {selectedRequest.website && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500">Website</p>
                    <a href={selectedRequest.website} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 hover:underline">
                      {selectedRequest.website}
                    </a>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-2">Areas of Expertise</p>
                <div className="flex flex-wrap gap-2">
                  {selectedRequest.areas_of_expertise?.map((area, idx) => (
                    <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                      {area}
                    </span>
                  ))}
                </div>
              </div>

              {selectedRequest.interested_in_premier && (
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                  <p className="font-semibold text-yellow-900 flex items-center">
                    <span className="text-2xl mr-2">🏆</span>
                    Interested in Premier Listing ($4.99/month)
                  </p>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500">Submitted</p>
                <p className="font-semibold">{new Date(selectedRequest.submitted_at).toLocaleString()}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add notes about this request..."
                />
              </div>

              {selectedRequest.status === 'pending' && (
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => handleAction(selectedRequest.id, 'approve', notes)}
                    disabled={actionLoading}
                    className="flex-1 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {actionLoading ? 'Processing...' : '✓ Approve & Create Listing'}
                  </button>
                  <button
                    onClick={() => handleAction(selectedRequest.id, 'reject', notes)}
                    disabled={actionLoading}
                    className="flex-1 px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:bg-gray-400"
                  >
                    {actionLoading ? 'Processing...' : '✗ Reject Request'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Listing Requests</h1>
            <p className="text-gray-600 mt-1">Review and approve auctioneer listing requests</p>
          </div>
        </div>

        {message && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-700">{message}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b">
            {['pending', 'approved', 'rejected', 'all'].map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`flex-1 px-6 py-4 font-medium capitalize ${filter === f ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-600 text-lg">No {filter !== 'all' ? filter : ''} requests found</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Auctioneer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">License</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Exp</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Premier?</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{req.full_name}</div>
                      <div className="text-sm text-gray-500">{req.email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm">{req.license_number}<br/><span className="text-gray-500">({req.state})</span></td>
                    <td className="px-6 py-4 text-sm">{req.city}, {req.state}</td>
                    <td className="px-6 py-4 text-sm">{req.years_experience} yrs</td>
                    <td className="px-6 py-4">
                      {req.interested_in_premier ? <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">🏆 Yes</span> : <span className="text-gray-400">No</span>}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : req.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {req.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(req.submitted_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      <button onClick={() => { setSelectedRequest(req); setShowModal(true); }} className="text-blue-600 hover:text-blue-900 font-medium">Review →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && <RequestModal />}
    </div>
  );
};
